import { HttpClient } from '@/shared/lib/http-client';
import { env } from '@/config/env';
import axios from 'axios';
import type {
  ClaimProfile,
  FacetsResponse,
  FilingSubmission,
  MeResponse,
  PackagesResponse,
  SettlementListItem,
  SettlementListParams,
  SettlementsResponse,
  SignedAuthorization,
} from './types';
import { toFilingProfile } from './domain/profile';

const PACKAGES_BATCH = 20;

function toApiSignatureMethod(method: string): 'draw' | 'typed' {
  return method === 'draw' ? 'draw' : 'typed';
}

function joinList(value: string | string[] | undefined): string | undefined {
  if (value == null) return undefined;
  const items = (Array.isArray(value) ? value : [value]).filter(Boolean);
  return items.length ? items.join(',') : undefined;
}

const settlementsCache = new Map<string, SettlementsResponse>();
const settlementsInflight = new Map<string, Promise<SettlementsResponse>>();
const allSettlementsCache = new Map<string, SettlementListItem[]>();
const allSettlementsInflight = new Map<string, Promise<SettlementListItem[]>>();

function settlementsCacheKey(params: SettlementListParams): string {
  const { token, ...filters } = params;
  return JSON.stringify({ ...compactParams(filters), authed: Boolean(token) });
}

function allSettlementsKey(params: SettlementListParams): string {
  return settlementsCacheKey({ ...params, offset: undefined });
}

function compactParams(params: SettlementListParams): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (params.popular) out.popular = true;
  if (params.actionable) out.actionable = true;
  if (params.q) out.q = params.q;
  if (params.sort) out.sort = params.sort;
  if (params.limit != null) out.limit = params.limit;
  if (params.offset != null) out.offset = params.offset;
  if (params.min_payout != null) out.min_payout = params.min_payout;
  if (params.max_payout != null) out.max_payout = params.max_payout;
  if (params.include_user_claims) out.include_user_claims = true;
  const categories = joinList(params.category);
  if (categories) out.category = categories;
  if (params.proof_required != null) out.proof_required = params.proof_required;
  if (params.status) out.status = params.status;
  if (params.deadline_before) out.deadline_before = params.deadline_before;
  if (params.deadline_after) out.deadline_after = params.deadline_after;
  if (params.has_deadline != null) out.has_deadline = params.has_deadline;
  const statuses = joinList(
    params.user_claim_status == null
      ? undefined
      : Array.isArray(params.user_claim_status)
        ? params.user_claim_status
        : [params.user_claim_status],
  );
  if (statuses) out.user_claim_status = statuses;
  return out;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

export class TurbopayApi extends HttpClient {
  constructor(baseURL: string) {
    super({ baseURL, timeout: 30_000 });
  }

  async listSettlements(params: SettlementListParams): Promise<SettlementsResponse> {
    const key = settlementsCacheKey(params);
    const cached = settlementsCache.get(key);
    if (cached) return cached;
    const pending = settlementsInflight.get(key);
    if (pending) return pending;

    const { token, ...filters } = params;
    const request = this.get<SettlementsResponse>('/v1/settlements', {
      params: compactParams(filters),
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }).then((res) => {
      settlementsCache.set(key, res);
      return res;
    }).finally(() => {
      settlementsInflight.delete(key);
    });
    settlementsInflight.set(key, request);
    return request;
  }

  /**
   * Onboarding catalog: POST questionnaire like iOS, GET fallback on 404/405.
   * `sort` stays `featured` (backend recommendation).
   */
  async listOnboardingSettlements(
    params: SettlementListParams,
    questionnaire: { answers: Record<string, string>; version: number },
  ): Promise<SettlementsResponse> {
    const key = settlementsCacheKey(params);
    const cached = settlementsCache.get(key);
    if (cached) return cached;
    const pending = settlementsInflight.get(key);
    if (pending) return pending;

    const { token, ...filters } = params;
    const query = compactParams(filters);
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const request = (async () => {
      try {
        const res = await this.post<SettlementsResponse>(
          '/v1/settlements',
          { questionnaire },
          { params: query, headers },
        );
        settlementsCache.set(key, res);
        return res;
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 404 || status === 405) {
          const res = await this.get<SettlementsResponse>('/v1/settlements', {
            params: query,
            headers,
          });
          settlementsCache.set(key, res);
          return res;
        }
        throw err;
      } finally {
        settlementsInflight.delete(key);
      }
    })();

    settlementsInflight.set(key, request);
    return request;
  }

  async listAllSettlements(params: SettlementListParams): Promise<SettlementListItem[]> {
    const key = allSettlementsKey(params);
    const cached = allSettlementsCache.get(key);
    if (cached) return cached;
    const pending = allSettlementsInflight.get(key);
    if (pending) return pending;

    const request = (async () => {
      const limit = params.limit ?? 100;
      let offset = params.offset ?? 0;
      const items: SettlementListItem[] = [];
      for (;;) {
        const res = await this.listSettlements({ ...params, limit, offset });
        const page = res.items ?? [];
        items.push(...page);
        if (!res.has_more || page.length === 0) break;
        offset += page.length;
        if (offset > 10_000) break;
      }
      allSettlementsCache.set(key, items);
      return items;
    })().finally(() => {
      allSettlementsInflight.delete(key);
    });
    allSettlementsInflight.set(key, request);
    return request;
  }

  async getFacets(): Promise<FacetsResponse> {
    return this.get<FacetsResponse>('/v1/facets');
  }

  async createPackages(settlementIds: string[]): Promise<PackagesResponse> {
    const unique = Array.from(new Set(settlementIds.filter(Boolean)));
    const items = [];
    let dataMode: string | undefined;
    for (const ids of chunk(unique, PACKAGES_BATCH)) {
      const res = await this.post<PackagesResponse>('/v1/filing/packages', {
        settlement_ids: ids,
      });
      dataMode = res.data_mode ?? dataMode;
      items.push(...(res.items ?? []));
    }
    return { data_mode: dataMode, items };
  }

  async downloadDocument(url: string): Promise<ArrayBuffer> {
    return this.getArrayBuffer(url);
  }

  async getMe(token: string): Promise<MeResponse> {
    return this.get<MeResponse>('/v1/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async submitFiling(params: {
    token: string;
    clientSubmissionId: string;
    profile: ClaimProfile;
    authorizations: SignedAuthorization[];
  }): Promise<FilingSubmission> {
    const data = await this.post<FilingSubmission | { submission?: FilingSubmission }>(
      '/v1/filing/submissions',
      {
        client_submission_id: params.clientSubmissionId,
        profile: toFilingProfile(params.profile),
        authorizations: params.authorizations.map((item) => ({
          ...item,
          signature_method: toApiSignatureMethod(item.signature_method),
        })),
      },
      {
        headers: {
          Authorization: `Bearer ${params.token}`,
          'Idempotency-Key': params.clientSubmissionId,
        },
        timeout: 60_000,
      },
    );
    if (data && typeof data === 'object' && 'submission' in data && data.submission) {
      return data.submission;
    }
    return data as FilingSubmission;
  }
}

export const turbopayApi = new TurbopayApi(env.VITE_TURBOPAY_API_URL);
